import i18n from '@/plugins/i18n'
import type { SearchFilterOption } from '@/components/Search/types.ts'

type RawStatusOption = {
  id?: any
  value?: any
  text?: any
  count?: any
}

const STATUS_ORDER: string[] = ['CUR', 'C', 'S', 'W', 'AP', 'AVS', 'P', 'O', 'T', 'RE', 'NA']

const STATUS_TO_I18N_KEY: Record<string, string> = {
  CUR: 'docStatusCur',
  C: 'docStatusC',
  CAN: 'docStatusC',
  S: 'docStatusS',
  SUP: 'docStatusS',
  AP: 'docStatusAP',
  AVS: 'docStatusAVS',
  P: 'docStatusP',
  O: 'docStatusO',
  T: 'docStatusT',
  W: 'docStatusW',
  RE: 'docStatusRE',
}

const STATUS_ALIASES: Record<string, string> = {
  CUR: 'CUR',
  CURRENT: 'CUR',
  C: 'C',
  CAN: 'C',
  CANCELLED: 'C',
  CANCELED: 'C',
  S: 'S',
  SUP: 'S',
  SUPERCEDED: 'S',
  SUPERSEDED: 'S',
  W: 'W',
  WITHDRAWN: 'W',
  AP: 'AP',
  AVS: 'AVS',
  P: 'P',
  O: 'O',
  T: 'T',
  RE: 'RE',
  REDESIGNATED: 'RE',
  NA: 'NA',
}

const VISIBLE_STATUS_CODES: string[] = ['CUR', 'CAN', 'SUP', 'W']
const VISIBLE_STATUS_CODE_SET = new Set<string>(VISIBLE_STATUS_CODES)
const VISIBLE_NORMALIZED_STATUS_CODE_SET = new Set<string>(
  VISIBLE_STATUS_CODES.map((code) => STATUS_ALIASES[code] ?? code),
)

const stripCountSuffix = (value: string): string => value.replace(/\s*\(\s*\d+\s*\)\s*$/, '').trim()
const getCountFromText = (value: string): number => {
  const match = value.match(/\(\s*(\d+)\s*\)\s*$/)
  if (!match) return 0
  const parsed = Number(match[1])
  return Number.isFinite(parsed) ? parsed : 0
}

export const normalizeStatusCode = (id: any, fallbackText: any = ''): string => {
  const rawId = String(id ?? '').trim().toUpperCase()
  if (rawId && STATUS_ALIASES[rawId]) {
    return STATUS_ALIASES[rawId]
  }

  const normalizedText = stripCountSuffix(String(fallbackText ?? '')).trim().toUpperCase()
  if (normalizedText && STATUS_ALIASES[normalizedText]) {
    return STATUS_ALIASES[normalizedText]
  }

  return rawId || normalizedText
}

const translateStatusText = (code: string, fallbackText: string): string => {
  const key = STATUS_TO_I18N_KEY[code]
  if (!key) {
    return fallbackText
  }
  const globalComposer: any = (i18n as any)?.global
  if (globalComposer?.te?.(key)) {
    return String(globalComposer.t(key))
  }
  return fallbackText
}

export const mapStatusOption = (
  option: RawStatusOption,
  includeCount: boolean = false,
): SearchFilterOption => {
  const id = String(option?.id ?? '')
  const sourceText = String(option?.value ?? option?.text ?? id)
  const rawText = stripCountSuffix(sourceText)
  const parsedCountFromText = getCountFromText(sourceText)
  const count = Number(option?.count ?? parsedCountFromText ?? 0)
  const statusCode = normalizeStatusCode(id, rawText)
  const translatedText = translateStatusText(statusCode, rawText)
  const text =
    includeCount && count > 0 ? `${translatedText} (${count})` : translatedText

  return { id, text }
}

export const getStatusDefaultOption = (statusValue: any): SearchFilterOption | null => {
  const rawStatusCode = String(statusValue ?? '').trim().toUpperCase()
  const normalizedStatusCode = normalizeStatusCode(rawStatusCode, rawStatusCode)
  if (!VISIBLE_NORMALIZED_STATUS_CODE_SET.has(normalizedStatusCode)) {
    return null
  }

  const defaultOption = mapStatusOption({ id: rawStatusCode, text: rawStatusCode })
  return defaultOption.id ? defaultOption : null
}

export const sortStatusOptions = (options: SearchFilterOption[]): SearchFilterOption[] => {
  const withIndex = options.map((option, index) => ({ option, index }))
  withIndex.sort((a, b) => {
    const aCode = normalizeStatusCode(a.option.id, a.option.text)
    const bCode = normalizeStatusCode(b.option.id, b.option.text)
    const aOrder = STATUS_ORDER.indexOf(aCode)
    const bOrder = STATUS_ORDER.indexOf(bCode)

    const safeA = aOrder >= 0 ? aOrder : Number.MAX_SAFE_INTEGER
    const safeB = bOrder >= 0 ? bOrder : Number.MAX_SAFE_INTEGER
    if (safeA !== safeB) return safeA - safeB
    return a.index - b.index
  })
  return withIndex.map((entry) => entry.option)
}

export const filterVisibleStatusOptions = (options: SearchFilterOption[]): SearchFilterOption[] =>
  sortStatusOptions(
    options.filter((option) =>
      VISIBLE_NORMALIZED_STATUS_CODE_SET.has(normalizeStatusCode(option?.id, option?.text)),
    ),
  )

export const getDefaultStatusOptions = (): SearchFilterOption[] =>
  VISIBLE_STATUS_CODES.map((code) => ({
    id: code,
    text: translateStatusText(code, code),
  }))

export const mergeStatusOptions = (options: SearchFilterOption[]): SearchFilterOption[] => {
  const merged = new Map<string, SearchFilterOption>()

  for (const option of getDefaultStatusOptions()) {
    const code = normalizeStatusCode(option.id, option.text)
    merged.set(code, { ...option })
  }

  for (const option of filterVisibleStatusOptions(options)) {
    const code = normalizeStatusCode(option.id, option.text)
    merged.set(code, {
      ...option,
      text: translateStatusText(code, option.text),
    })
  }

  return filterVisibleStatusOptions(Array.from(merged.values()))
}
